import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton, Stack, Typography } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { BlTooltip } from "Components/_Shared/BlTooltip";
import { Flex } from "Components/_Shared/Core/Flex";
import type { PersonPickerPerson } from "Components/_Shared/Inputs/PersonPicker/personPickerPersons";
import { FormPersonPicker } from "Forms/Components/FormPersonPicker";
import type { IncomeBulkFormType } from "Forms/Schemas/Budget/Incomes/incomeBulkFormSchema";
import type { IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { IncomeDraftFields } from "./IncomeDraftFields";

const ACTION_SIZE = 32;
const ACTION_ICON_SIZE = 18;

type Props = {
  readonly index: number;
  readonly guid?: string;
  readonly incomeType: IncomeIncomeType;
  readonly persons: PersonPickerPerson[];
  readonly isReadOnly: boolean;
  readonly onRemove: () => void;
};

export const IncomeDraftCard: FC<Props> = ({ index, guid, incomeType, persons, isReadOnly, onRemove }) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);
  const {
    control,
    formState: { errors },
  } = useFormContext<IncomeBulkFormType>();

  const draftErrors = errors.drafts?.[index];
  const isDisabled = readOnly || isReadOnly;
  const removeLabel = t(Resources.AnalysisDetail.Budget.Incomes.Form.Buttons.RemoveItem);

  return (
    <Stack
      data-income-guid={guid}
      data-invalid={!!draftErrors}
      spacing={Spacing.L}
      py={Spacing.L}
      px={Spacing.XL}
      bgcolor="common.white"
      border={1}
      borderColor={draftErrors ? "error.main" : "divider"}
      borderRadius={Radius.L}
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gap={Spacing.M}>
        <FormPersonPicker
          control={control}
          name={`drafts.${index}.clientGuid`}
          persons={persons}
          caption={t(Resources.AnalysisDetail.Budget.Incomes.IncomeBelongsToCaption)}
          required
          disabled={isDisabled}
        />

        {!isDisabled && (
          <BlTooltip title={removeLabel} enterDelay={300} enterNextDelay={300}>
            <IconButton
              aria-label={removeLabel}
              onClick={onRemove}
              sx={{
                flexShrink: 0,
                width: ACTION_SIZE,
                height: ACTION_SIZE,
                borderRadius: Radius.M,
                color: "text.secondary",
                "& .MuiSvgIcon-root": { fontSize: ACTION_ICON_SIZE },
                "&:hover": { bgcolor: "error.light", color: "error.main" },
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </BlTooltip>
        )}
      </Flex>

      {draftErrors?.message && (
        <Typography variant="body2" color="error.main">
          {draftErrors.message}
        </Typography>
      )}

      <IncomeDraftFields index={index} incomeType={incomeType} disabled={isDisabled} />
    </Stack>
  );
};
